import * as XLSX from "xlsx";
import { s2ab } from "./s2ab";
import { saveAs } from "file-saver";
import { i1 } from "@/i18n";

interface ExecutiveSummaryTableRow {
    metric: string;
    position: string;
    your_dealership: string;
    your_peers: string;
    all_dealerships: string;
    ranking?: React.ReactNode | string;
    rankingValue?: number | null;
}

interface ExecutiveSummaryExportData {
    Position: string;
    Metric: string;
    "Your Dealership": string | number;
    "Your Peers": string | number;
    "All Dealerships": string | number;
    _format: string; // Hidden field for formatting
}

interface CleanExecutiveSummaryData {
    Position: string;
    Metric: string;
    "Your Dealership": string;
    "Your Peers": string;
    "All Dealerships": string;
}

// Helper function to format cell values and apply Excel formatting
const formatCell = (worksheet: XLSX.WorkSheet, cellAddress: string, format: string) => {
    const cell = worksheet[cellAddress];
    if (!cell || cell.v === null || cell.v === undefined) return;

    const cellValue = cell.v;
    if (typeof cellValue !== "string" || cellValue === "–" || cellValue === "—") return;

    let excelFormat: string;

    // Determine actual format from the cell content if format is mixed
    let actualFormat = format;
    if (format === "mixed") {
        if (cellValue.includes("$")) {
            actualFormat = "currency";
        } else if (cellValue.includes("%")) {
            actualFormat = "percent";
        } else if (cellValue.includes("Years") || /^\d+\.\d+$/.test(cellValue.trim())) {
            actualFormat = "decimal";
        } else {
            return; // Can't determine format, skip
        }
    }

    switch (actualFormat) {
        case "currency":
            excelFormat = '"$"#,##0';
            break;
        case "percent":
            excelFormat = "0.0%";
            break;
        case "decimal":
            excelFormat = "0.0";
            break;
        default:
            return;
    }

    cell.v = cellValue;
    cell.z = excelFormat;
};

// Create consolidated data from the executive summary table
const consolidateData = (tableData: ExecutiveSummaryTableRow[]): ExecutiveSummaryExportData[] => {
    return tableData.map((row) => {
        // Determine format based on metric type - handle mixed columns by detecting from content
        let format = "text";

        // Check if this is a known metric type
        if (row.metric === i1.t("common.metrics.avgCompensation")) {
            format = "currency";
        } else if (row.metric.includes("Retention") || row.metric.includes("Turnover")) {
            format = "percent";
        } else if (row.metric.includes("Tenure")) {
            format = "decimal";
        } else {
            // For unknown metrics, use mixed format to let formatCell determine from content
            format = "mixed";
        }

        return {
            Position: row.position,
            Metric: row.metric,
            "Your Dealership": row.your_dealership,
            "Your Peers": row.your_peers,
            "All Dealerships": row.all_dealerships,
            _format: format,
        };
    });
};

export const exportExecutiveSummaryData = (
    tableData: ExecutiveSummaryTableRow[],
    filetype: string = "Excel"
) => {
    try {
        const rawData = consolidateData(tableData);

        if (rawData.length > 0) {
            // Remove hidden columns and create clean export data
            const cleanData: CleanExecutiveSummaryData[] = rawData.map((row) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { _format, ...cleanRow } = row;
                return cleanRow as CleanExecutiveSummaryData;
            });

            // Create single worksheet from clean data
            const worksheet = XLSX.utils.json_to_sheet(cleanData);

            // Save file
            if (filetype === "CSV") {
                // For CSV, don't apply any formatting - keep original values
                const csvContent = XLSX.utils.sheet_to_csv(worksheet);
                const csvBlob = new Blob([csvContent], { type: "text/csv" });
                saveAs(csvBlob, `executive_summary_report.csv`);
            } else if (filetype === "Excel") {
                // Apply formatting only for Excel
                const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
                const dataColumns = [2, 3, 4]; // Your Dealership, Your Peers, All Dealerships

                for (let row = 1; row <= range.e.r; row++) {
                    const format = rawData[row - 1]?._format;

                    if (format === "mixed") {
                        // For mixed format, evaluate each cell individually
                        dataColumns.forEach((col) => {
                            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                            formatCell(worksheet, cellAddress, "mixed");
                        });
                    } else {
                        // For consistent format, apply to all data columns
                        dataColumns.forEach((col) => {
                            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                            formatCell(worksheet, cellAddress, format);
                        });
                    }
                }

                // Create workbook and add the worksheet
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Executive Summary");

                // Export workbook
                const excelBlob = new Blob(
                    [s2ab(XLSX.write(workbook, { bookType: "xlsx", type: "binary" }))],
                    { type: "application/octet-stream" }
                );
                saveAs(excelBlob, `executive_summary_report.xlsx`);
            }
        }
    } catch (e) {
        console.log(e);
    }
};
